import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import numeral from 'numeral';
import PageTitle from '../common/PageTitle';
import InfoCard from './Info';
import SalesDynamicsChart from './SalesDynamicsChart';
import TurnoverChart from './TurnoverChart';
import SalesChannelsChart from './SalesChannelsChart';
import AverageCheckChart from './AverageCheckChart';
import OrdersQuantityChart from './OrdersQuantityChart';
import Table from './Table';

import { fetchSummary, fetchChangeUsedData } from '../../actions/summary';

import { getCurrencySymbol } from '../../helpers';
import styled from '@emotion/styled';
import { toast } from 'react-toastify';
import moment from 'moment';
import OrdersDynamicsChart from './OrdersDynamicsChart';
import MarginSalesChart from './MarginSalesChart';
import DeliveryDynamicsChart from './DeliveryDynamicsChart';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

const keyToLabel = {
  Mon: `Понедельник`,
  Tue: `Вторник`,
  Wed: `Среда`,
  Thu: `Четверг`,
  Fri: `Пятница`,
  Sat: `Суббота`,
  Sun: `Воскресенье`,

  Jan: `Январь`,
  Feb: `Февраль`,
  Mar: `Март`,
  Apr: `Апрель`,
  May: `Май`,
  Jun: `Июнь`,
  Jul: `Июль`,
  Aug: `Август`,
  Sep: `Сентябрь`,
  Oct: `Октябрь`,
  Nov: `Ноябрь`,
  Dec: `Декабрь`,

  Пн: `Понедельник`,
  Вт: `Вторник`,
  Ср: `Среда`,
  Чт: `Четверг`,
  Пт: `Пятница`,
  Сб: `Суббота`,
  Вс: `Воскресенье`,

  Янв: `Январь`,
  Фев: `Февраль`,
  Мар: `Март`,
  Апр: `Апрель`,
  Май: `Май`,
  Июн: `Июнь`,
  Июл: `Июль`,
  Авг: `Август`,
  Сен: `Сентябрь`,
  Окт: `Октябрь`,
  Нов: `Ноябрь`,
  Дек: `Декабрь`,
};

const isResponseReady = (object) => {
  let ready = true;
  Object.values(object).forEach((item) => {
    if (item && typeof item === 'object') {
      Object.values(item).forEach((item) => {
        if (item === null) ready = false;
      });
    }
  });
  return ready;
};

const formatNumber = (number) => {
  if (typeof number === `string`) number = parseInt(number.replace(/\s/g, ''));
  switch (true) {
    case number >= 10 ** 3 && number < 10 ** 4:
      return `${numeral(number / 10 ** 3).format('0[.]0')}K`;
    case number >= 10 ** 4 && number < 10 ** 6:
      return `${numeral(number / 10 ** 3).format('0')}K`;
    case number >= 10 ** 6 && number < 10 ** 7:
      return `${numeral(number / 10 ** 6).format('0[.]0')}M`;
    case number >= 10 ** 7:
      return `${numeral(number / 10 ** 6).format('0')}M`;
    default:
      return `${number}`;
  }
};

const Wrapper = styled.div`
  .warning {
    display: flex;
  }

  .link {
    background: none;
    border: none;
    padding: 0;
    margin-left: auto;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (min-width: 1200px) {
    .col-xl-4 {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }

  @media (min-width: 1440px) {
    .col-xl-4 {
      flex: 0 0 50%;
      max-width: 50%;
    }
  }

  @media (min-width: 1950px) {
    .col-xl-4 {
      flex: 0 0 33.33333%;
      max-width: 33.33333%;
    }
  }

  @media (max-width: 767px) {
    margin: 0 -22px;

    h4 {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .page-title-box {
      padding: 0 24px;
    }

    .warning {
      display: block;
      margin-bottom: 24px;
      border-radius: unset;

      .text-danger {
        display: block;
        margin-top: 13px;
        margin-left: 0;
      }
    }

    .card-box,
    .card {
      width: 100%;
      border-radius: unset;
      box-shadow: none;
    }
  }
`;

const SummaryPage = () => {
  const { summary } = useSelector((state) => state.summary);
  const dispatch = useDispatch();
  const [responseReady, setResponseReady] = useState();
  const {
    report_sales_sum_1,
    report_orders_sum_1,
    report_sales_avg_order_1,
    report_orders_sale_percent_1,
    report_sales_dynamic_1,
    report_sales_dynamic_1_delivered,
    report_orders_dynamic_1,
    report_orders_dynamic_1_with_canceled,
    report_goods_turnover_1,
    report_sales_channels_1,
    report_sales_avg_order_full_1,
    report_orders_sum_full_1,
    report_top_selling_products_1,
    report_last_changed_orders_1,
    report_delivery_channels_1,
    updateTs,
    report_goods_margin_1,
    report_goods_margin_1_delivered,
  } = summary;

  const { checkoutSettings } = useSelector((state) => state.settings);
  const { reportsUseDemoData } = useSelector((state) => state.auth.account);
  const currencySymbol = getCurrencySymbol(checkoutSettings.currency);

  const { t } = useAutoTranslation();

  useEffect(() => {
    dispatch(fetchSummary());
  }, [dispatch, reportsUseDemoData]);

  useEffect(() => {
    setResponseReady(isResponseReady(summary));
  }, [summary]);

  const channelSalesSorted = report_sales_channels_1.channelSales.sort(
    (a, b) => b.amount - a.amount,
  );
  const channelSalesTop = report_sales_channels_1.channelSales.slice(0, 4);
  const channelSalesOther = {
    channel: t(`Прочие`),
    amount: channelSalesSorted.slice(4).reduce((prev, curr) => prev + curr.amount, 0),
  };

  const handleDeleteData = async () => {
    await dispatch(fetchChangeUsedData(false))
      .then(() => toast.info(t(`Демо-данные для вашего аккаунта удалены`)))
      .catch(() => toast.error(t(`Ошибка удаления демо-данных`)));
  };

  const monthly = {
    totalSales: 123.45,
    totalPurchases: 12.45,
    totalCosts: 123.45,
    totalIncome: 123.45,
    margin: 123.45,
    point: null,
  };

  const generateWeekly = () => {
    const days = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
    const arr = [];
    for (let i = 0; i <= 7; i++) {
      const obj = Object.create(monthly);
      obj.totalSales = 123.45;
      obj.totalPurchases = 12.45;
      obj.totalCosts = 123.45;
      obj.totalIncome = 123.45;
      obj.margin = 123.45;
      obj.point = days[i];

      arr.push(obj);
    }

    return arr;
  };

  const generateMonths = () => {
    const arr = [];
    for (let i = 1; i <= 31; i++) {
      const obj = Object.create(monthly);

      obj.totalSales = 123.45;
      obj.totalPurchases = 12.45;
      obj.totalCosts = 123.45;
      obj.totalIncome = 123.45;
      obj.margin = 123.45;
      obj.point = i;

      arr.push(obj);
    }

    return arr;
  };

  const generateAnnually = () => {
    const months = [
      'Янв',
      'Фев',
      'Мар',
      'Апр',
      'Май',
      'Июн',
      'Июл',
      'Авг',
      'Сент',
      'Окт',
      'Ноя',
      'Дек',
    ];
    const arr = [];
    for (let i = 0; i <= 12; i++) {
      const obj = Object.create(monthly);
      obj.totalSales = 123.45;
      obj.totalPurchases = 12.45;
      obj.totalCosts = 123.45;
      obj.totalIncome = 123.45;
      obj.margin = 123.45;
      obj.point = months[i];

      arr.push(obj);
    }

    return arr;
  };

  return (
    <Wrapper>
      <PageTitle
        title={t('Сводка')}
        extraStr={
          updateTs &&
          `${t('Обновлено')}: ${moment(updateTs).format(`DD.MM.YYYY`)} ${moment(updateTs).format(
            `HH:mm`,
          )}`
        }
      />
      {reportsUseDemoData && (
        <div className="alert alert-warning warning">
          <i className="mdi mdi-alert-outline mr-2" />
          {t('В данный момент в сводке используются демонстрационные данные')}
          <button className="text-danger link" onClick={handleDeleteData}>
            {t('Удалить демо-данные')}
          </button>
        </div>
      )}
      {responseReady ? (
        <>
          <div className="row">
            <InfoCard
              title={t('Продажи')}
              primaryGap={t('сегодня')}
              primaryValue={
                report_sales_sum_1.today !== undefined &&
                `${currencySymbol} ${formatNumber(report_sales_sum_1.today)}`
              }
              secondaryGap={t('За неделю')}
              secondaryValue={`${currencySymbol} ${formatNumber(report_sales_sum_1.week)}`}
              delta={report_sales_sum_1.diffPercent}
              positiveDelta={report_sales_sum_1.diffDirection === `UP`}
            />
            <InfoCard
              title={t('Заказы')}
              primaryGap={t('сегодня')}
              primaryValue={report_orders_sum_1.today.toLocaleString()}
              secondaryGap={t('За неделю')}
              secondaryValue={`${report_orders_sum_1.week.toLocaleString()} шт.`}
              delta={report_orders_sum_1.diffPercent}
              positiveDelta={report_orders_sum_1.diffDirection === `UP`}
            />
            <InfoCard
              title={t('Средний чек')}
              primaryGap={t('за 30 дней')}
              primaryValue={
                report_sales_avg_order_1.month !== undefined &&
                `${currencySymbol} ${formatNumber(report_sales_avg_order_1.month)}`
              }
              secondaryGap={t('За всё время')}
              secondaryValue={`${currencySymbol} ${formatNumber(report_sales_avg_order_1.allTime)}`}
              delta={report_sales_avg_order_1.diffPercent}
              positiveDelta={report_sales_avg_order_1.diffDirection === `UP`}
            />
            <InfoCard
              title={t('Процент выкупа')}
              primaryGap={t('за 30 дней')}
              primaryValue={
                report_orders_sale_percent_1.month !== undefined &&
                `${report_orders_sale_percent_1.month} %`
              }
              secondaryGap={t('За всё время')}
              secondaryValue={`${report_orders_sale_percent_1.allTime}%`}
              delta={report_orders_sale_percent_1.diffPercent}
              positiveDelta={report_orders_sale_percent_1.diffDirection === `UP`}
            />
          </div>
          <div className="row">
            <SalesDynamicsChart
              salesDynamicDelivered={report_sales_dynamic_1_delivered}
              salesDynamicUndelivered={report_sales_dynamic_1}
              keyToLabel={keyToLabel}
              formatNumber={formatNumber}
              currencySymbol={currencySymbol}
            />
            <OrdersDynamicsChart
              ordersDynamic={report_orders_dynamic_1}
              ordersDynamicWithCancelled={report_orders_dynamic_1_with_canceled}
              keyToLabel={keyToLabel}
              formatNumber={formatNumber}
              currencySymbol={currencySymbol}
            />
            <MarginSalesChart
              title={t('Маржинальность продаж')}
              shipped={report_goods_margin_1}
              delivered={report_goods_margin_1_delivered}
            />
            <TurnoverChart
              title={t('Оборот товаров')}
              thisWeekSales={report_goods_turnover_1.week}
              previousWeekSales={report_goods_turnover_1.previousWeek}
              previousWeekPositiveDelta={report_goods_turnover_1.previousWeekDiffDirection === `UP`}
              previousMonthSales={report_goods_turnover_1.previousMonth}
              previousMonthPositiveDelta={
                report_goods_turnover_1.previousMonthDiffDirection === `UP`
              }
              weeklyData={report_goods_turnover_1.weeklyData}
              annuallyData={report_goods_turnover_1.annuallyData}
              keyToLabel={keyToLabel}
              formatNumber={formatNumber}
            />
            <SalesChannelsChart
              title={t('Каналы продажи')}
              top={channelSalesTop}
              other={channelSalesOther.amount !== undefined && channelSalesOther}
              formatNumber={formatNumber}
            />
            <AverageCheckChart
              title={t('Средний чек')}
              weeklyValue={report_sales_avg_order_full_1.week}
              monthlyValue={report_sales_avg_order_full_1.month}
              allTimeValue={report_sales_avg_order_full_1.allTime}
              data={report_sales_avg_order_full_1.annuallyData}
              keyToLabel={keyToLabel}
              formatNumber={formatNumber}
            />
            <OrdersQuantityChart
              title={t('Количество заказов')}
              weeklyValue={report_orders_sum_full_1.week}
              monthlyValue={report_orders_sum_full_1.month}
              allTimeValue={report_orders_sum_full_1.allTime}
              data={report_orders_sum_full_1.annuallyData}
              keyToLabel={keyToLabel}
              formatNumber={formatNumber}
            />
          </div>
          <div className="row">
            <Table
              title={t('Самые продаваемые товары')}
              headers={[t(`Товар`), t(`Стоимость`), t(`Количество`), t(`Сумма`)]}
              items={report_top_selling_products_1.products}
              formatNumber={formatNumber}
            />
            <Table
              title={t('История заказов')}
              headers={[t(`Заказ`), t(`Обновлен`), t(`Статус`), t(`Сумма`)]}
              items={report_last_changed_orders_1.orders}
              formatNumber={formatNumber}
              link="/orders/"
            />
          </div>
          <div className="row">
            <DeliveryDynamicsChart
              title={t('Каналы доставки')}
              weeklyValue={report_delivery_channels_1?.weeklyData}
              monthlyValue={report_delivery_channels_1?.monthlyData}
              annuallyData={report_delivery_channels_1?.annuallyData}
              formatNumber={formatNumber}
              currencySymbol={currencySymbol}
            />
          </div>
        </>
      ) : (
        <div className="alert alert-warning" role="alert">
          <i className="mdi mdi-alert-outline mr-2"></i>
          {t('Ваш отчёт ещё не сформирован')}
        </div>
      )}
    </Wrapper>
  );
};

export default SummaryPage;
